/**
 * OAuth return helpers (FRE-683).
 *
 * After the browser returns from `/api/v1/auth/oauth/<provider>/callback`,
 * the backend has set httpOnly `accessToken`/`refreshToken` cookies and 302'd
 * to the frontend's `redirect_url`. The frontend cannot read those cookies,
 * so we signal a successful OAuth round-trip by appending a short-lived
 * query parameter (`OAUTH_RETURN_PARAM`) to the redirect URL.
 *
 * `AppInitializer` reads this indicator on mount, calls `bootstrapSession()`
 * to force a fresh `/auth/me` fetch (bypassing the localStorage hint gate),
 * and then strips the parameter from the URL via `history.replaceState` so a
 * reload does not re-trigger the bootstrap.
 *
 * OAuth initiation (FRE-680 / FRE-681 → `lib/oauth/redirect.ts`) must include
 * `OAUTH_RETURN_PARAM` in the `redirect_url` it sends to the backend so the
 * final navigation back to the frontend carries the indicator.
 */

/** Query parameter name that marks the current URL as a post-OAuth return. */
export const OAUTH_RETURN_PARAM = 'oauth_session';

/**
 * Guards browser-only APIs (`window`, `history`) during SSR so these helpers
 * can be imported from server components without crashing at module load.
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Returns `true` when the current window URL contains the OAuth return
 * indicator. SSR-safe: returns `false` when `window` is undefined.
 */
export function isOAuthReturn(): boolean {
  if (!isBrowser()) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has(OAUTH_RETURN_PARAM);
  } catch {
    return false;
  }
}

/**
 * Removes `OAUTH_RETURN_PARAM` from the current URL without triggering a
 * navigation or re-render. Uses `history.replaceState` so Next.js router
 * state is untouched. SSR-safe.
 */
export function clearOAuthReturnIndicator(): void {
  if (!isBrowser()) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(OAUTH_RETURN_PARAM)) return;
    url.searchParams.delete(OAUTH_RETURN_PARAM);
    const nextSearch = url.searchParams.toString();
    const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // history.replaceState may throw in edge cases (detached iframe, etc.) —
    // swallow so the caller can still complete the bootstrap flow.
  }
}
